/**
 * DetacheringDossier — canonical, read-only §-sectie-dossier in de detachering-stijl.
 *
 * Vervangt de oude DossierModal als de standaard dossier-weergave. Toont een
 * overlay (portal naar document.body) met de classified §-look uit
 * packages/dossier: genummerde secties §01..§05, mono-koppen, accent-lijn.
 *
 * Props:
 *   engineer  — { id, name, role, photo, skills:[], beschikbaarheid:{...},
 *                 dossier:{ profileSummary, specialisations, serviceRecord,
 *                           projects, education, languages, contact } }
 *   onClose   — sluit-callback (zelfde open/close-patroon als DossierModal)
 *
 * Read-only: geen contenteditable, geen localStorage. Defensief tegen ontbrekende
 * velden (photo / serviceRecord / projects / languages kunnen ontbreken).
 */
import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { availabilityBadge } from './matching/availability';
import './detacheringDossier.css';

function Section({ index, title, children }) {
  return (
    <section className="dd-block">
      <h3 className="dd-block-title">
        <span className="dd-block-idx">§{String(index).padStart(2, '0')}</span>
        <span className="dd-block-name">{title}</span>
        <span className="dd-block-line" aria-hidden />
      </h3>
      {children}
    </section>
  );
}

export default function DetacheringDossier({ engineer, onClose }) {
  useEffect(() => {
    const handleEsc = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handleEsc);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  if (!engineer) return null;

  const dossier = engineer.dossier || {};
  const skills = engineer.skills || [];
  const specialisations = dossier.specialisations || [];
  const serviceRecord = dossier.serviceRecord || [];
  const projects = dossier.projects || [];
  const education = dossier.education || [];
  const languages = dossier.languages || [];
  const email = dossier.contact?.email;

  const initials = (engineer.name || '?')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0])
    .join('')
    .toUpperCase();

  const avail = availabilityBadge(engineer.beschikbaarheid);
  // Dossiernummer: gebruik _index indien aanwezig (landing), anders de id-prefix.
  const cardNumber =
    typeof engineer._index === 'number'
      ? String(engineer._index + 1).padStart(3, '0')
      : (engineer.id || '').slice(0, 3).toUpperCase() || '000';

  // Badges: skills + specialisations, ontdubbeld, beperkt voor leesbaarheid.
  const badges = Array.from(new Set([...skills, ...specialisations])).slice(0, 12);

  return createPortal(
    <div className="dd-overlay" onClick={onClose}>
      <div
        className="detachering-dossier"
        data-theme="intel"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={`Dossier ${engineer.name}`}
      >
        <button className="dd-close" onClick={onClose} aria-label="Sluiten">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>

        {/* Classified strip */}
        <div className="dd-strip">
          <span><span className="dd-strip-pill">●</span> CLASSIFIED · INTERN GEBRUIK — HUMANAIZE</span>
          <span>DOSSIER NL-{cardNumber}-K</span>
        </div>

        <article className="dd-card">
          {/* Header: foto + identiteit + beschikbaarheid */}
          <header className="dd-header">
            <div className="dd-photo-frame">
              <div className="dd-photo">
                {engineer.photo
                  ? <img src={engineer.photo} alt={engineer.name} />
                  : <div className="dd-photo-fallback">{initials}</div>}
                <span className="dd-corner dd-corner-tl" aria-hidden />
                <span className="dd-corner dd-corner-tr" aria-hidden />
                <span className="dd-corner dd-corner-bl" aria-hidden />
                <span className="dd-corner dd-corner-br" aria-hidden />
              </div>
            </div>

            <div className="dd-identity">
              <span className="dd-id-label">Detacheringsdossier</span>
              <h1 className="dd-name">{engineer.name}</h1>
              {engineer.role && <p className="dd-role">{engineer.role}</p>}

              {avail && (
                <div className="dd-avail">
                  <span className={`dd-avail-dot dd-avail-dot--${avail.statusKey}`} aria-hidden />
                  <span className="dd-avail-status">{avail.title}</span>
                </div>
              )}

              {badges.length > 0 && (
                <div className="dd-badges">
                  {badges.map((b, i) => (
                    <span key={i} className="dd-badge">
                      <span className="dd-badge-mark">{'//'}</span>{b}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </header>

          <div className="dd-sections">
            {/* §01 INTRODUCTIE */}
            {dossier.profileSummary && (
              <Section index={1} title="INTRODUCTIE">
                <p className="dd-lead-text">{dossier.profileSummary}</p>
              </Section>
            )}

            {/* §02 EXPERTISE */}
            {(skills.length > 0 || specialisations.length > 0) && (
              <Section index={2} title="EXPERTISE">
                {skills.length > 0 && (
                  <div className="dd-skills">
                    {skills.map((s, i) => (
                      <span key={`sk-${i}`} className="dd-skill-pill">
                        <span className="dd-skill-mark">{'//'}</span>{s}
                      </span>
                    ))}
                  </div>
                )}
                {specialisations.length > 0 && (
                  <ul className="dd-spec-list">
                    {specialisations.map((s, i) => (
                      <li key={`sp-${i}`} className="dd-spec">{s}</li>
                    ))}
                  </ul>
                )}
              </Section>
            )}

            {/* §03 WERKERVARING */}
            {(serviceRecord.length > 0 || projects.length > 0) && (
              <Section index={3} title="WERKERVARING">
                {serviceRecord.length > 0 && (
                  <div className="dd-entries">
                    {serviceRecord.map((r, i) => (
                      <div key={`sr-${i}`} className="dd-entry">
                        <div className="dd-entry-row">
                          <span className="dd-entry-role">{r.role}</span>
                          {r.period && <span className="dd-entry-period">{r.period}</span>}
                        </div>
                        {r.org && <span className="dd-entry-org">{r.org}</span>}
                        {r.description && <span className="dd-entry-detail">{r.description}</span>}
                      </div>
                    ))}
                  </div>
                )}
                {projects.length > 0 && (
                  <>
                    <span className="dd-subhead">Projecten</span>
                    <div className="dd-entries">
                      {projects.map((p, i) => (
                        <div key={`pr-${i}`} className="dd-entry">
                          <span className="dd-entry-role">{p.title}</span>
                          {p.summary && <span className="dd-entry-detail">{p.summary}</span>}
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </Section>
            )}

            {/* §04 OPLEIDING */}
            {education.length > 0 && (
              <Section index={4} title="OPLEIDING">
                <div className="dd-entries">
                  {education.map((edu, i) => (
                    <div key={`ed-${i}`} className="dd-entry">
                      <div className="dd-entry-row">
                        <span className="dd-entry-role">{edu.degree}</span>
                        {edu.year && <span className="dd-entry-period">{edu.year}</span>}
                      </div>
                      {edu.school && <span className="dd-entry-org">{edu.school}</span>}
                    </div>
                  ))}
                </div>
              </Section>
            )}

            {/* §05 TALEN & CONTACT */}
            {(languages.length > 0 || email) && (
              <Section index={5} title="TALEN & CONTACT">
                {languages.length > 0 && (
                  <div className="dd-skills">
                    {languages.map((l, i) => (
                      <span key={`la-${i}`} className="dd-skill-pill">
                        <span className="dd-skill-mark">{'//'}</span>{l}
                      </span>
                    ))}
                  </div>
                )}
                {email && (
                  <div className="dd-contact">
                    <span className="dd-contact-key">E-MAIL</span>
                    <a className="dd-contact-val" href={`mailto:${email}`}>{email}</a>
                  </div>
                )}
              </Section>
            )}
          </div>
        </article>
      </div>
    </div>,
    document.body
  );
}
